// Row plumbing shared by the engine and the runner. Pure: no browser APIs.

import type { Field, FieldResult, GoogleCut, InputRow, ProfileResult, SerpSizeConfig } from './types';
import { normalize } from './text';
import type { ProfileSet } from './profiles';
import { assembleField, errorField } from './assemble';

export const FIELDS: Field[] = ['title', 'description'];

export interface EngineField {
  field: Field;
  original: string;
  cut: GoogleCut;
  results: ProfileResult[];
}

/** Engine output for one row on one engine's profiles (before warnings). */
export interface EngineRow {
  url: string;
  error?: string;
  fields: EngineField[];
}

export type RowCuts = Partial<Record<Field, GoogleCut>> | null | undefined;

export function rowUrl(row: InputRow, index: number): string {
  return row.url && row.url.trim() ? row.url.trim() : `row-${index + 1}`;
}

export function rowError(row: InputRow): string | null {
  if (normalize(row.title) || normalize(row.description)) return null;
  return row.error?.trim() || 'no title or description';
}

/** Merge engine outputs for the same row from several engines, ordering results by `order`. */
export function mergeEngineRows(parts: EngineRow[], order: string[]): EngineRow {
  const base = parts[0];
  if (base.error) return base;
  const rank = new Map(order.map((id, i) => [id, i]));
  const fields: EngineField[] = base.fields.map(f => ({ ...f, results: [...f.results] }));
  for (const part of parts.slice(1)) {
    for (const f of part.fields) {
      const target = fields.find(x => x.field === f.field);
      if (target) target.results.push(...f.results);
      else fields.push({ ...f, results: [...f.results] });
    }
  }
  for (const f of fields) f.results.sort((a, b) => (rank.get(a.profile) ?? 1e9) - (rank.get(b.profile) ?? 1e9));
  return { url: base.url, fields };
}

/** Pure assembly of a row's engine output into FieldResults with warnings. */
export function assembleRow(r: EngineRow, row: InputRow, runBrand: string | undefined, profiles: ProfileSet, config: SerpSizeConfig): FieldResult[] {
  if (r.error) return [errorField(r.url, 'title', r.error)];
  const brand = row.brand?.trim() || runBrand;
  return r.fields.map(f =>
    assembleField({
      url: r.url,
      field: f.field,
      original: f.original,
      cut: f.cut,
      results: f.results,
      brand,
      targetKeyword: row.targetKeyword,
      profiles: profiles.byId,
      config,
    }),
  );
}
